import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CustomTour, CustomTourInquiry } from '../types';
import AdminNavbar from '../components/admin/AdminNavbar';
import { fetchAllCustomTours } from '../services/customTourService';
import { fetchCustomTourInquiries } from '../services/customTourInquiryService';

const AdminCustomTourInterestsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const tourIdFromUrl = searchParams.get('tourId') || '';
  const [customTours, setCustomTours] = useState<CustomTour[]>([]);
  const [inquiries, setInquiries] = useState<CustomTourInquiry[]>([]);
  const [selectedTourId, setSelectedTourId] = useState(tourIdFromUrl);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [toursData, inquiryData] = await Promise.all([
          fetchAllCustomTours(),
          fetchCustomTourInquiries(),
        ]);
        setCustomTours(toursData);
        setInquiries(inquiryData);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load custom tour interests';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (tourIdFromUrl) {
      setSelectedTourId(tourIdFromUrl);
    }
  }, [tourIdFromUrl]);

  const filteredInquiries = useMemo(() => {
    if (!selectedTourId) {
      return inquiries;
    }
    return inquiries.filter((inquiry) => inquiry.customTourId === selectedTourId);
  }, [inquiries, selectedTourId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--secondary-pearl)]">
        <AdminNavbar />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary-deep-blue)] mx-auto mb-4"></div>
            <p className="body-regular text-[var(--secondary-slate)]">Loading custom tour interests...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--secondary-pearl)]">
        <AdminNavbar />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <strong className="font-bold">Error:</strong> {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--secondary-pearl)]">
      <AdminNavbar />
      <div className="py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="heading-primary text-[var(--primary-deep-blue)] mb-2">
                Custom tour interests
              </h1>
              <p className="body-regular text-[var(--secondary-slate)]">
                View visitor interest for each custom tour destination
              </p>
            </div>

            <div className="card mb-6">
              <div className="card-content">
                <div className="flex items-center gap-2">
                  <label htmlFor="customTourFilter" className="body-medium text-[var(--primary-deep-blue)] font-semibold whitespace-nowrap">
                    Custom Tour:
                  </label>
                  <select
                    id="customTourFilter"
                    value={selectedTourId}
                    onChange={(e) => setSelectedTourId(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--primary-deep-blue)] focus:border-transparent"
                  >
                    <option value="">All</option>
                    {customTours.map((tour) => (
                      <option key={tour.id} value={tour.id}>
                        {tour.country}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-content p-0">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-solid-amber text-white">
                        <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Name</th>
                        <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Email</th>
                        <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Phone</th>
                        <th className="border border-gray-300 px-4 py-3 text-left font-semibold">Group Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInquiries.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="border border-gray-300 px-4 py-8 text-center text-gray-500">
                            No custom tour interests found
                          </td>
                        </tr>
                      ) : (
                        filteredInquiries.map((inquiry) => (
                          <tr key={inquiry.id} className="hover:bg-amber-50">
                            <td className="border border-gray-300 px-4 py-3">{inquiry.name || '—'}</td>
                            <td className="border border-gray-300 px-4 py-3">{inquiry.email}</td>
                            <td className="border border-gray-300 px-4 py-3">{inquiry.phoneNumber}</td>
                            <td className="border border-gray-300 px-4 py-3">{inquiry.numberOfPeople}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mt-4 text-center">
              <p className="body-regular text-[var(--secondary-slate)]">
                Showing {filteredInquiries.length} of {inquiries.length} interests
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCustomTourInterestsPage;
